import { userDisplayName } from "@common-models/user";
import { Avatar, Tooltip, Typography } from "@mui/material";
import { CSSProperties, useMemo, useState, useEffect } from "react";
import { groupcolors } from "@common/theme";
import { useImageProxy } from "@common/context/ImageProxyContext";
import { isClearFlow } from '@common/utils/clear-flow';

export interface PersonAvatarProps {
  isName?: boolean;
  size?: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  profileImg?: string;
  toolTip?: boolean;
  textSize?: number;
  style?: CSSProperties;
  bgcolor?: string;
  textWidth?: string;
  avatarStyle?: CSSProperties;
  onImageError?: () => void;
}

export const PersonAvatar = (props: PersonAvatarProps) => {
  const { processUrl } = useImageProxy();
  const [imgSrc, setImgSrc] = useState<string | undefined>(props.profileImg);

  const fullName = userDisplayName(props);
  const person = { fullName: fullName, email: props.email }
  
  const generateInitials = (person: { fullName?: string; email?: string }) => {
    if (person?.fullName && person.fullName !== '') {
      const namesAsList: string[] = person.fullName.split(" ");
      if (namesAsList.length === 2) {
        const initials = `
        ${namesAsList[0]?.slice(0, 1)}${namesAsList[1]?.slice(0, 1)}`;
        return initials;
      } else {
        return `${namesAsList[0]?.slice(0, 1)}`;
      }
    } else {
      return person.email?.slice(0, 1);
    }
  };

  const tooltip = (fullName || props?.email) ?? ''

  const getDeterministicColor = (email: string) => {
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    return groupcolors[Math.abs(hash) % groupcolors.length];
  };

  const bgColor = useMemo(() => {
    if (props.bgcolor) return props.bgcolor;
    // The hash palette is an identity cue for telling teammates apart. The clear
    // flow only ever shows the signed-in user's own avatar, where a random palette
    // colour just reads as off-brand — so use the brand token there instead.
    if (isClearFlow()) return 'var(--cta-button-bg)';
    return getDeterministicColor(props.email ?? '');
  }, [props.bgcolor, props.email]);

  useEffect(() => {
    let ignore = false;
    if (props.profileImg) {
        // Only process if it looks like a URL that needs proxying?
        // For now, let the provider decide.
        processUrl(props.profileImg)
          .then((result) => { if (!ignore) setImgSrc(result); })
          .catch(() => { if (!ignore) setImgSrc(props.profileImg); });
    } else {
        setImgSrc(undefined);
    }
    return () => { ignore = true; };
  }, [props.profileImg, processUrl]);

  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", ...props.style }}>

      <Tooltip arrow placement="bottom" disableInteractive title={props?.toolTip ? tooltip : ""}>
        <Avatar
          style={{
            width: props.size ? props.size : 28,
            height: props.size ? props.size : 28,
            fontSize: props.textSize ? props.textSize : 12,
            color: "var(--color-always-white)",
            fontWeight: "bold",
            backgroundColor: bgColor,
            boxShadow: 'none',
            textTransform: 'uppercase',
            border: 'none',
            ...props.avatarStyle,
          }}
          src={imgSrc}
          imgProps={{
            onError: () => {
              setImgSrc(undefined);
              props.onImageError?.();
            }
          }}
        >
          {(!imgSrc || groupcolors.indexOf(imgSrc) !== -1) &&
            generateInitials(person)
          }
        </Avatar>
      </Tooltip>


      {props.isName && (
        <Typography variant="inherit" noWrap
          sx={{
            margin: "0 0 0 10px",
            width: props?.textWidth ?? 140,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "var(--tasks-text-secondary)",
          }}
        >
          {fullName}
        </Typography>
      )}
    </div>
  );
}

export default PersonAvatar;
